/* eslint-disable no-param-reassign */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import {
  ADD_MORE_SECTION_LIST,
  defaultResumeBuilderState,
  DEFAULT_EDUCATION_FIELDS,
  DEFAULT_SPACING,
  DEFAULT_TWO_COLUMN_LAYOUT,
  FontList,
  getDefaultSectionLayout,
  ResumeThemes,
} from 'src/constants/resumeBuilder';
import {
  applyEnhancedToResume,
  mapVersionDataToExistingData,
} from 'src/helpers/resumeBuilder';
import type {
  GetResumeBuildeVersionsSub,
  GetResumeBuilderResponseById,
} from 'src/openapi';
import type { RootState } from 'src/redux/index';
import type {
  LayoutSectionKey,
  ResumeDetails,
  ResumeDraftState,
  Theme,
} from '../types';

/** Which optional sections the editor should already be showing.
 *
 * `addedExtraSections` is only ever appended by a click on an "Add More Fields"
 * chip, so nothing restored it on load: a reloaded resume offered Publications
 * and Languages as sections to add while the preview was already rendering
 * their entries. A section holding entries has plainly been added, so every
 * load path derives the list from the content -- and keeps whatever the writer
 * added by hand, which may still be empty. */
const withPopulatedExtraSections = (
  state: ResumeDraftState,
): ResumeDraftState => {
  const populated = ADD_MORE_SECTION_LIST.map((item) => item.value).filter(
    (key) => {
      const section = state.resumeDetails?.[key];
      return Array.isArray(section) ? section.length > 0 : !!section;
    },
  );
  const merged = Array.from(
    new Set([...(state.addedExtraSections ?? []), ...populated]),
  );

  return merged.length === (state.addedExtraSections ?? []).length
    ? state
    : { ...state, addedExtraSections: merged };
};

/** Finishes loading another version into the draft: the react-hook-form editor
 * has to re-read the swapped-in JSON. */
const settleLoadedVersion = (next: ResumeDraftState): ResumeDraftState =>
  withPopulatedExtraSections({
    ...next,
    resyncNonce: (next.resyncNonce || 0) + 1,
  });

export const initialState: ResumeDraftState = {
  resumeDetails: defaultResumeBuilderState,
  settings: {
    theme: ResumeThemes[0],
    fontFamily: FontList[0],
    fontSize: 'm',
    scale: 1,
    page: 'A4',
    mode: 'customise',
    view: 'Original',
    sectionLayout: DEFAULT_TWO_COLUMN_LAYOUT,
    skillsStyle: 'chips',
    educationFields: DEFAULT_EDUCATION_FIELDS,
    spacing: DEFAULT_SPACING,
  },
  premiumPreviewSettings: {
    template: null,
    fontFamily: null,
    fontSize: null,
    totalCredit: 0,
  },
  currentStep: 'personalDetails',
  currentUpdating: 'personalDetails',
  isEditing: true,
  alreadyHaveResume: null,
  progress: 0,
  completedSections: [],
  totalDownloadsCount: 0,
  unlockedTemplates: [],
  selectedTemplateCategory: 'All Templates',
  isEnhanceResumeDataLoading: false,
  enhancedResumeData: null,
  addedExtraSections: [],
  enhanceResumePromptList: [],
  analysis: null,
  resyncNonce: 0,
  isDownloadingResume: false,
};

const resumeBuilderSlice = createSlice({
  name: 'resumeBuilder',
  initialState,
  reducers: {
    setAlreadyHaveResume: (state, action) => {
      state.alreadyHaveResume = action.payload;
    },
    updateSettingMode: (state, action) => {
      state.settings.mode = action.payload;
    },
    updateIsEditing: (state, action) => {
      state.isEditing = action.payload;
    },
    updateCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    updateCurrentUpdating: (state, action) => {
      state.currentUpdating = action.payload;
    },
    updateCompletedSections: (state, action) => {
      state.completedSections = action.payload;
    },
    updateProgress: (state, action) => {
      state.progress = action.payload;
    },
    updatePersonalDetails: (state, action) => {
      state.resumeDetails.personalDetails = action.payload;
    },
    updateEducationDetails: (state, action) => {
      state.resumeDetails.education = action.payload;
    },
    updateExperienceDetails: (state, action) => {
      state.resumeDetails.experience = action.payload;
    },
    updateLicensesAndCertificationDetails: (state, action) => {
      state.resumeDetails.licensesAndCertification = action.payload;
    },
    updateSkillsDetails: (state, action) => {
      state.resumeDetails.skills = action.payload;
    },
    updateShortBioDetails: (state, action) => {
      state.resumeDetails.shortBio = action.payload;
    },
    updateProjectsDetails: (state, action) => {
      state.resumeDetails.projects = action.payload;
    },
    updateInterestOrHobbiesDetails: (state, action) => {
      state.resumeDetails.interestOrHobbies = action.payload;
    },
    updatelanguagesDetails: (state, action) => {
      state.resumeDetails.languages = action.payload;
    },
    updatePublications: (state, action) => {
      state.resumeDetails.publications = action.payload;
    },
    updatePositionsOfResponsibility: (state, action) => {
      state.resumeDetails.positionsOfResponsibility = action.payload;
    },
    updateVolunteerExperiences: (state, action) => {
      state.resumeDetails.volunteerExperiences = action.payload;
    },
    updateAwardsAndAchievements: (state, action) => {
      state.resumeDetails.awardsAndAchievements = action.payload;
    },
    updateTotalDownloadCount: (state, action) => {
      state.totalDownloadsCount += action.payload;
    },
    applySavedResume: (
      state,
      action: PayloadAction<{
        id?: string;
        completedSections: (keyof ResumeDetails)[];
      }>,
    ) => {
      state.id = action.payload.id;
      state.completedSections = action.payload.completedSections;
    },
    updateResumeTitle: (state, action) => {
      state.resumeDetails.title = action.payload;
    },
    updateResumeTemplate: (state, action) => {
      if (action.payload) {
        const previousName = state.resumeDetails.template?.name;
        state.resumeDetails.template = action.payload;
        // Switching to a structurally different template resets the section
        // layout to that template's default so single-column templates never
        // inherit a two-column layout (which would drop the right-column sections).
        if (action.payload.name && action.payload.name !== previousName) {
          state.settings.sectionLayout = getDefaultSectionLayout(
            action.payload.name,
          );
        }
      }
    },
    updateResumeDetail: (state, action) => {
      const next = { ...state, ...action.payload };
      // Resumes created before dynamic layout (and several open-resume entry
      // points) carry settings without `sectionLayout`. Backfill a template
      // default so consumers never read `hiddenSections` off undefined.
      if (next.settings && !next.settings.sectionLayout) {
        next.settings = {
          ...next.settings,
          sectionLayout: getDefaultSectionLayout(
            next.resumeDetails?.template?.name ?? '',
          ),
        };
      }
      return withPopulatedExtraSections(next);
    },
    toggleTheme: (state, action: PayloadAction<Theme>) => {
      state.settings.theme = action.payload;
    },
    updateOriginalTheme: (state, action: PayloadAction<Theme | undefined>) => {
      state.settings.originalTheme = action.payload;
    },
    updateSelectedTemplateCategory: (state, action) => {
      state.selectedTemplateCategory = action.payload;
    },
    setIsEnhanceResumeDataLoading: (state, action) => {
      state.isEnhanceResumeDataLoading = action.payload;
    },
    setEnhancedResumeData: (state, action) => {
      state.enhancedResumeData = action.payload;
    },
    toggleTemplateView: (state, action) => {
      state.settings.view = action.payload;
    },
    updatePremiumPreviewSettings: (state, action) => {
      state.premiumPreviewSettings = {
        ...state.premiumPreviewSettings,
        ...action.payload,
      };
    },
    editEnhancedResumeExperienceDescription: (state, action) => {
      const { index, description } = action.payload as {
        index: number;
        description: string;
      };
      if (
        state.resumeDetails?.experience &&
        state.resumeDetails.experience[index]
      ) {
        state.resumeDetails.experience[index].description_gpt = description;
      }
    },
    editEnhancedResumeProjectDescription: (state, action) => {
      const { index, description } = action.payload as {
        index: number;
        description: string;
      };
      if (
        state.resumeDetails?.projects &&
        state.resumeDetails.projects[index]
      ) {
        state.resumeDetails.projects[index].description_gpt = description;
      }
    },
    editEnhancedResumeShortBioDescription: (state, action) => {
      if (state.resumeDetails?.shortBio) {
        state.resumeDetails.enhancedShortBio = action.payload;
      }
    },
    // Applies a freshly-enhanced document (raw getResumeBuilderDocument DTO)
    // onto the editable draft -- the AI text becomes the working content the
    // user edits directly, on a new version (the pre-enhancement version
    // stays in history to revert to).
    applyEnhancedResumeVersion: (
      state,
      action: PayloadAction<GetResumeBuilderResponseById>,
    ) => {
      applyEnhancedToResume(
        state.resumeDetails,
        action.payload.enhanced_resume_metadata,
      );
      state.resumeDetails.currentVersion = {
        id: action.payload.id!,
        version_no: action.payload.version_no!,
      };
      state.enhancedResumeData = null;
      state.settings.view = 'Original';
      state.isEnhanceResumeDataLoading = false;
    },
    // Replaces the whole draft with a past version (raw getResumeBuilderDocument
    // DTO) -- mapVersionDataToExistingData reads `state` directly rather than
    // taking a serialized copy of it as an action payload.
    replaceDraftFromVersion: (
      state,
      action: PayloadAction<GetResumeBuilderResponseById>,
    ) =>
      settleLoadedVersion(mapVersionDataToExistingData(action.payload, state)),
    // Restore: puts a past version's content back on the *current* version
    // rather than moving the draft onto the old one, so the history in between
    // survives and the next autosave writes the restored JSON forward.
    restoreDraftFromVersion: (
      state,
      action: PayloadAction<{
        document: GetResumeBuilderResponseById;
        targetVersion: GetResumeBuildeVersionsSub;
      }>,
    ) => {
      const next = settleLoadedVersion(
        mapVersionDataToExistingData(action.payload.document, state),
      );
      next.resumeDetails.currentVersion = action.payload.targetVersion;
      return next;
    },
    addExtaSectionsInResume: (state, action) => {
      if (!state.addedExtraSections.includes(action.payload)) {
        state.addedExtraSections = [
          ...state.addedExtraSections,
          action.payload,
        ];
      }
    },
    updateResumeFontFamily: (state, action) => {
      state.settings.fontFamily = action.payload;
    },
    updateResumeFontSize: (state, action) => {
      state.settings.fontSize = action.payload;
    },
    updateResumeSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },
    updateCurrentVersion: (state, action) => {
      state.resumeDetails.currentVersion = action.payload;
    },
    updatePremiumFeatureApplied: (state, action) => {
      state.premiumFeatureApplied = action.payload;
    },
    addToEnhanceResumePromptList: (state, action) => {
      state.enhanceResumePromptList.push(action.payload);
    },
    setIsDownloadingResume: (state, action: PayloadAction<boolean>) => {
      state.isDownloadingResume = action.payload;
    },
    setSkillsStyle: (state, action: PayloadAction<'chips' | 'bullets'>) => {
      state.settings.skillsStyle = action.payload;
    },
    toggleEducationField: (
      state,
      action: PayloadAction<keyof typeof DEFAULT_EDUCATION_FIELDS>,
    ) => {
      const fields = state.settings.educationFields || {
        ...DEFAULT_EDUCATION_FIELDS,
      };
      state.settings.educationFields = {
        ...fields,
        [action.payload]: !fields[action.payload],
      };
    },
    moveSectionToColumn: (
      state,
      action: PayloadAction<{
        section: LayoutSectionKey;
        targetColumn: 'left' | 'right';
      }>,
    ) => {
      const { section, targetColumn } = action.payload;
      const sourceColumn = targetColumn === 'left' ? 'right' : 'left';
      state.settings.sectionLayout[sourceColumn] = state.settings.sectionLayout[
        sourceColumn
      ].filter((s) => s !== section);
      if (!state.settings.sectionLayout[targetColumn].includes(section)) {
        state.settings.sectionLayout[targetColumn].push(section);
      }
    },
    reorderSectionInColumn: (
      state,
      action: PayloadAction<{
        column: 'left' | 'right';
        from: number;
        to: number;
      }>,
    ) => {
      const { column, from, to } = action.payload;
      const cols = [...state.settings.sectionLayout[column]];
      const [moved] = cols.splice(from, 1);
      cols.splice(to, 0, moved);
      state.settings.sectionLayout[column] = cols;
    },
    toggleSectionVisibility: (
      state,
      action: PayloadAction<LayoutSectionKey>,
    ) => {
      const section = action.payload;
      const hidden = state.settings.sectionLayout.hiddenSections;
      if (hidden.includes(section)) {
        state.settings.sectionLayout.hiddenSections = hidden.filter(
          (s) => s !== section,
        );
      } else {
        state.settings.sectionLayout.hiddenSections = [...hidden, section];
      }
    },
  },
});

const { actions, reducer } = resumeBuilderSlice;

export const {
  updateSettingMode,
  updateIsEditing,
  updateCurrentStep,
  updateCurrentUpdating,
  updateCompletedSections,
  updateProgress,
  updatePersonalDetails,
  updateEducationDetails,
  updateExperienceDetails,
  updateLicensesAndCertificationDetails,
  updateSkillsDetails,
  updateShortBioDetails,
  updateProjectsDetails,
  updateInterestOrHobbiesDetails,
  updatelanguagesDetails,
  updatePublications,
  updatePositionsOfResponsibility,
  updateVolunteerExperiences,
  updateAwardsAndAchievements,
  updateTotalDownloadCount,
  applySavedResume,
  updateResumeTitle,
  updateResumeTemplate,
  setAlreadyHaveResume,
  updateResumeDetail,
  toggleTheme,
  toggleTemplateView,
  updateSelectedTemplateCategory,
  setEnhancedResumeData,
  setIsEnhanceResumeDataLoading,
  editEnhancedResumeExperienceDescription,
  editEnhancedResumeProjectDescription,
  editEnhancedResumeShortBioDescription,
  applyEnhancedResumeVersion,
  replaceDraftFromVersion,
  restoreDraftFromVersion,
  addExtaSectionsInResume,
  updateResumeFontFamily,
  updateResumeFontSize,
  updatePremiumPreviewSettings,
  updateResumeSettings,
  updateCurrentVersion,
  updatePremiumFeatureApplied,
  updateOriginalTheme,
  addToEnhanceResumePromptList,
  moveSectionToColumn,
  reorderSectionInColumn,
  toggleSectionVisibility,
  setIsDownloadingResume,
  setSkillsStyle,
  toggleEducationField,
} = actions;

export const resumeBuilderSelector = (state: RootState) => state.resumeBuilder;

export default reducer;
